/**
 * Feature Matrix — 4-6 feature tiles with circular icons + springy stagger.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem, sceneHeader } from "./_mockHelpers.js";

export const featureMatrix: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Lokal|Region|map", "0€|Setup|bolt", "12%|Flat|chart", "DSGVO|Schwarzwald|lock"]).slice(0, 6);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const isVertical = ctx.format === "9:16";

  const wrapId = `${clipPrefix}-wrap`;
  const tileIds = items.map((_, i) => `${clipPrefix}-tile-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const header = sceneHeader({
    clipPrefix, accent, eyebrow, title: headline, titlePx: lg * 0.78, subPx: sm * 0.95,
  });

  const cols = items.length <= 2 ? 2 : items.length <= 4 ? 2 : isVertical ? 2 : 3;

  const tiles = items.map((raw, i) => {
    const [title, desc, icon] = splitItem(raw);
    return `<div id="${tileIds[i]}" style="background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};border-radius:32px;padding:32px;display:flex;flex-direction:column;gap:14px;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);box-shadow:0 24px 60px rgba(0,0,0,0.35), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.1)};min-height:200px;">
      <div style="width:64px;height:64px;border-radius:9999px;background:linear-gradient(140deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 100%);display:flex;align-items:center;justify-content:center;color:${themeColors.bg};box-shadow:0 0 30px ${withAlpha(accent, 0.55)};">${lucideIcon(icon ?? "star", 28, themeColors.bg, 2.4)}</div>
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.62}px;letter-spacing:-1px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(title ?? "")}</div>
      ${desc ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.78}px;color:${withAlpha(themeColors.fg, 0.78)};line-height:1.4;">${escapeHtml(desc)}</div>` : ""}
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:72px;">
    ${header.html}
    <div style="display:grid;grid-template-columns:repeat(${cols}, minmax(0, 1fr));gap:18px;width:100%;max-width:${cols * 360}px;">${tiles}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    ...tileIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 32, scale: 0.92 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  tileIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: "raw:\"back.out(1.4)\"" }, 0.45 + i * 0.12)));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
